import { TextSelection } from "@tiptap/pm/state";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";

// Structural subset of the browser Selection (or ProseMirror's
// domSelectionRange) that selection resolution needs.
export interface SelectionRangeLike {
  anchorNode: Node | null;
  anchorOffset: number;
  focusNode: Node | null;
  focusOffset: number;
  isCollapsed: boolean;
  toString?: () => string;
}

// Structural subset of prosemirror-view's EditorView. `docView.posFromDOM` is
// a private API, but unlike the public `posAtDOM` it returns -1 instead of
// throwing, and it is what ProseMirror's own selectionFromDOM uses to read
// the browser selection.
export interface ShareLinkEditorViewLike {
  dom: HTMLElement;
  docView: { posFromDOM: (node: Node, offset: number, bias: number) => number };
  domSelectionRange?: () => SelectionRangeLike | null;
}

export interface ResolvedShareLinkSelection {
  from: number;
  to: number;
}

const nodeIsInsideEditor = (editorDom: HTMLElement, node: Node | null): boolean => {
  if (!node) return false;
  const element = node.nodeType === Node.TEXT_NODE ? node.parentNode : node;
  return Boolean(element && editorDom.contains(element));
};

/**
 * Resolve a non-empty editor selection for shared-link comment creation.
 * Read-only ProseMirror state lags behind the browser DOM for backward
 * (bottom-to-top) drags, and normalized Range endpoints lose backward
 * whole-paragraph selections, so mirror ProseMirror's internal
 * selectionFromDOM instead: resolve the selection's anchor/focus nodes with
 * bias 1 and clamp to valid text positions via TextSelection.between.
 */
export const resolveShareLinkEditorSelection = (view: ShareLinkEditorViewLike, doc: ProseMirrorNode): ResolvedShareLinkSelection | null => {
  if (!view?.dom || !doc) return null;

  const domSel = typeof view.domSelectionRange === "function" ? view.domSelectionRange() : window.getSelection();
  if (!domSel || domSel.isCollapsed) return null;

  const { anchorNode, focusNode } = domSel;
  if (!anchorNode || !focusNode) return null;
  if (!nodeIsInsideEditor(view.dom, focusNode) || !nodeIsInsideEditor(view.dom, anchorNode)) {
    return null;
  }

  const domText = (domSel.toString?.() ?? "").trim();
  if (!domText) return null;

  try {
    const anchorPos = view.docView.posFromDOM(anchorNode, domSel.anchorOffset, 1);
    const focusPos = view.docView.posFromDOM(focusNode, domSel.focusOffset, 1);
    if (anchorPos < 0 || focusPos < 0 || anchorPos === focusPos) {
      return null;
    }

    const $anchor = doc.resolve(anchorPos);
    const $head = doc.resolve(focusPos);
    const bias = focusPos >= anchorPos ? 1 : -1;
    const { from, to } = TextSelection.between($anchor, $head, bias);
    if (doc.textBetween(from, to, " ").trim().length === 0) {
      return null;
    }

    return { from, to };
  } catch (_) {
    // posFromDOM/resolve can throw for nodes outside the editor content.
    return null;
  }
};
